/**
 * First-Run Guide (H-2026-09-13-136 A3) — the one-time 3-card onboarding
 * overlay for first-time visitors: pick an image / pick a style / wear it
 * (task card §1.2).
 *
 * localStorage key `mamamiya_first_run_done`: ANY value means "seen" and the
 * guide never auto-shows again. The Help rail button is the replay entry
 * (card A3 'Replay guide'): it clears the marker, then shows the guide —
 * replay never depends on the auto-show gate.
 *
 * Singleton by design: showing the guide replaces any open one. Esc is
 * handled in the CAPTURE phase + stopPropagation (same pattern as
 * components/drawer.js): main.js binds a bubble-phase Esc that asks the host
 * to close the whole workspace in embed iframes — an open guide must swallow
 * the key before it reaches that handler.
 */

import { esc, escAttr } from '../../utils/escape.js'

export const FIRST_RUN_KEY = 'mamamiya_first_run_done'

// UI copy allowlist — all Chinese UI strings for this module live ONLY here
// (same pattern as PAGE_FRAMES in page-frame.js). Everything else is code.
export const FIRST_RUN_COPY = {
  steps: [
    { icon: 'ph-image', title: 'Pick an image', desc: 'Upload your photo, or grab one from the wall' },
    { icon: 'ph-palette', title: 'Pick a style', desc: 'Four styles in one go — tap the one you like' },
    { icon: 'ph-t-shirt', title: 'Wear it', desc: 'See it on the product, order the one you love' },
  ],
  done: 'Got it — start',
}

// localStorage can throw (private mode, disabled storage) — every access is
// guarded so the guide degrades to "always show" instead of breaking boot.
function getStorage() {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** Whether the visitor has already seen (or dismissed) the guide. */
export function hasSeenFirstRun(storage = getStorage()) {
  if (!storage) return false
  try {
    return storage.getItem(FIRST_RUN_KEY) !== null
  } catch {
    return false
  }
}

/** Mark the guide as seen. Any stored value satisfies the gate. */
export function markFirstRunDone(storage = getStorage()) {
  if (!storage) return
  try {
    storage.setItem(FIRST_RUN_KEY, String(Date.now()))
  } catch {
    /* storage full / private mode — the guide may re-show next visit */
  }
}

/** Clear the seen marker (Help 'Replay guide' step 1: clear marker). */
export function resetFirstRunGuide(storage = getStorage()) {
  if (!storage) return
  try {
    storage.removeItem(FIRST_RUN_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Pure: guide markup - 3 step cards in a row + the 'Got it, start' done
 * button. Steps default to FIRST_RUN_COPY.steps; injectable for tests.
 */
export function renderFirstRunGuide(steps = FIRST_RUN_COPY.steps) {
  const cards = steps
    .map(
      (s, i) => `<div class="frg__card">
        <i class="ph ${escAttr(s.icon)} frg__icon" aria-hidden="true"></i>
        <span class="frg__num">${i + 1}</span>
        <h4 class="frg__title">${esc(s.title)}</h4>
        <p class="frg__desc">${esc(s.desc)}</p>
      </div>`
    )
    .join('')
  return `<div class="frg__panel" role="dialog" aria-modal="true" aria-label="Welcome guide">
    <div class="frg__cards">${cards}</div>
    <button type="button" class="frg__done">${esc(FIRST_RUN_COPY.done)}</button>
  </div>`
}

// The one live guide: { overlay, onDone, onKeydown }
let active = null

export function isFirstRunGuideOpen() {
  return active !== null
}

/**
 * Show the guide overlay unconditionally (replay path included — the caller
 * owns the seen-gate). Returns the overlay element. The done button and Esc
 * both mark the guide as seen before closing.
 */
export function showFirstRunGuide({ onDone, root = document } = {}) {
  if (active) closeFirstRunGuide()
  const overlay = root.createElement('div')
  overlay.className = 'frg'
  overlay.innerHTML = renderFirstRunGuide()
  overlay.querySelector('.frg__done').addEventListener('click', () => closeFirstRunGuide())
  const onKeydown = (e) => {
    if (e.key !== 'Escape') return
    // Swallow before main.js's bubble-phase workspace-close sees it.
    e.stopPropagation()
    closeFirstRunGuide()
  }
  document.addEventListener('keydown', onKeydown, true)
  active = { overlay, onDone, onKeydown }
  root.body.appendChild(overlay)
  overlay.querySelector('.frg__done').focus?.()
  return overlay
}

/** Close the live guide (no-op when none) and mark it seen. */
export function closeFirstRunGuide() {
  if (!active) return
  const { overlay, onDone, onKeydown } = active
  active = null
  document.removeEventListener('keydown', onKeydown, true)
  overlay.remove()
  markFirstRunDone()
  if (onDone) {
    try {
      onDone()
    } catch (e) {
      console.error('[FirstRunGuide] onDone error:', e)
    }
  }
}

/**
 * Auto-show gate: show ONLY when the visitor has never seen the guide.
 * Returns the overlay element, or null when the marker exists.
 */
export function maybeShowFirstRunGuide(opts = {}) {
  if (hasSeenFirstRun()) return null
  return showFirstRunGuide(opts)
}

/**
 * Boot wiring: auto-show on first visit + bind the Help rail button as the
 * replay entry (clear marker → show). Idempotent — safe to call once per
 * boot even if the help button is re-rendered by a host.
 */
export function initFirstRunGuide({ root = document } = {}) {
  maybeShowFirstRunGuide({ root })
  const helpBtn = root.querySelector('#helpBtn')
  if (helpBtn && !helpBtn.dataset.frgWired) {
    helpBtn.dataset.frgWired = '1'
    helpBtn.addEventListener('click', () => {
      resetFirstRunGuide()
      showFirstRunGuide({ root })
    })
  }
}

export default {
  initFirstRunGuide,
  maybeShowFirstRunGuide,
  showFirstRunGuide,
  closeFirstRunGuide,
  resetFirstRunGuide,
  hasSeenFirstRun,
  markFirstRunDone,
}
